import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Star, ThumbsUp, MessageSquare, Filter } from 'lucide-react';
import { Header } from '../components/ui/Header';
import { Rating } from '../components/ui/Rating';
interface Review {
  id: string;
  buyerName: string;
  rating: number;
  comment: string;
  productName: string;
  date: string;
  helpful: number;
  replied: boolean;
}
export function VendorReviewsScreen() {
  const [filterRating, setFilterRating] = useState<number | 'all'>('all');
  // Mock reviews
  const reviews: Review[] = [{
    id: 'R1',
    buyerName: 'John Doe',
    rating: 5,
    comment: 'Excellent product! Fast delivery and great quality. Highly recommended!',
    productName: 'iPhone 14 Pro Max',
    date: new Date().toISOString(),
    helpful: 12,
    replied: true
  }, {
    id: 'R2',
    buyerName: 'Jane Smith',
    rating: 4,
    comment: 'Good phone but packaging could be better. Overall satisfied with the purchase.',
    productName: 'Samsung Galaxy S23 Ultra',
    date: new Date(Date.now() - 86400000).toISOString(),
    helpful: 5,
    replied: false
  }, {
    id: 'R3',
    buyerName: 'Mike Johnson',
    rating: 3,
    comment: 'Product is okay but delivery took longer than expected.',
    productName: 'iPhone 14 Pro Max',
    date: new Date(Date.now() - 172800000).toISOString(),
    helpful: 2,
    replied: false
  }];
  const filteredReviews = filterRating === 'all' ? reviews : reviews.filter(r => r.rating === filterRating);
  const avgRating = reviews.reduce((sum, r) => sum + r.rating, 0) / reviews.length;
  const ratingDistribution = [5, 4, 3, 2, 1].map(rating => ({
    rating,
    count: reviews.filter(r => r.rating === rating).length,
    percentage: reviews.filter(r => r.rating === rating).length / reviews.length * 100
  }));
  return <motion.div initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} className="min-h-screen bg-gray-50 pb-24" data-id="element-2279">
      <Header title="Reviews & Ratings" data-id="element-2280" />

      <div className="p-4 space-y-4" data-id="element-2281">
        {/* Rating Summary */}
        <div className="bg-white p-5 rounded-2xl shadow-sm border border-gray-50" data-id="element-2282">
          <div className="flex items-center gap-6 mb-4" data-id="element-2283">
            <div className="text-center" data-id="element-2284">
              <p className="text-4xl font-bold text-gray-900 mb-1" data-id="element-2285">
                {avgRating.toFixed(1)}
              </p>
              <Rating value={avgRating} size="sm" data-id="element-2286" />
              <p className="text-xs text-gray-500 mt-1" data-id="element-2287">
                {reviews.length} reviews
              </p>
            </div>

            <div className="flex-1 space-y-2" data-id="element-2288">
              {ratingDistribution.map(dist => <div key={dist.rating} className="flex items-center gap-2" data-id="element-2289">
                  <span className="text-xs text-gray-600 w-3" data-id="element-2290">
                    {dist.rating}
                  </span>
                  <Star className="w-3 h-3 text-accent fill-accent" data-id="element-2291" />
                  <div className="flex-1 h-2 bg-gray-100 rounded-full overflow-hidden" data-id="element-2292">
                    <div className="h-full bg-accent rounded-full" style={{
                  width: `${dist.percentage}%`
                }} data-id="element-2293" />
                  </div>
                  <span className="text-xs text-gray-500 w-6" data-id="element-2294">
                    {dist.count}
                  </span>
                </div>)}
            </div>
          </div>
        </div>

        {/* Filter */}
        <div className="flex gap-2 overflow-x-auto" data-id="element-2295">
          <button onClick={() => setFilterRating('all')} className={`px-4 py-2 rounded-xl text-sm font-medium whitespace-nowrap transition-colors ${filterRating === 'all' ? 'bg-primary text-white' : 'bg-white text-gray-600 border border-gray-200'}`} data-id="element-2296">
            All ({reviews.length})
          </button>
          {[5, 4, 3, 2, 1].map(rating => <button key={rating} onClick={() => setFilterRating(rating)} className={`px-4 py-2 rounded-xl text-sm font-medium whitespace-nowrap transition-colors flex items-center gap-1 ${filterRating === rating ? 'bg-primary text-white' : 'bg-white text-gray-600 border border-gray-200'}`} data-id="element-2297">
              {rating} <Star className="w-3 h-3" data-id="element-2298" />
            </button>)}
        </div>

        {/* Reviews List */}
        <div className="space-y-3" data-id="element-2299">
          {filteredReviews.map((review, idx) => <motion.div key={review.id} initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: idx * 0.05
        }} className="bg-white p-4 rounded-2xl shadow-sm border border-gray-50" data-id="element-2300">
              <div className="flex items-start justify-between mb-2" data-id="element-2301">
                <div data-id="element-2302">
                  <p className="font-medium text-gray-900" data-id="element-2303">
                    {review.buyerName}
                  </p>
                  <p className="text-xs text-gray-500" data-id="element-2304">
                    {new Date(review.date).toLocaleDateString('en-US', {
                  month: 'short',
                  day: 'numeric',
                  year: 'numeric'
                })}
                  </p>
                </div>
                <Rating value={review.rating} size="sm" data-id="element-2305" />
              </div>

              <p className="text-sm text-gray-600 mb-2" data-id="element-2306">{review.productName}</p>
              <p className="text-sm text-gray-900 mb-3" data-id="element-2307">{review.comment}</p>

              <div className="flex items-center gap-4 pt-3 border-t border-gray-100" data-id="element-2308">
                <button className="flex items-center gap-1 text-sm text-gray-500 hover:text-primary" data-id="element-2309">
                  <ThumbsUp className="w-4 h-4" data-id="element-2310" />
                  <span data-id="element-2311">{review.helpful} helpful</span>
                </button>
                <button className="flex items-center gap-1 text-sm text-primary font-medium" data-id="element-2312">
                  <MessageSquare className="w-4 h-4" data-id="element-2313" />
                  <span data-id="element-2314">{review.replied ? 'View Reply' : 'Reply'}</span>
                </button>
              </div>
            </motion.div>)}
        </div>
      </div>
    </motion.div>;
}